// src/odoo/customers/api/overviewApi.ts
// All comments are in English.

import { http } from "../../../core/http";

// ---------- Shared date range ----------

export interface DateRangeQuery {
  // Inclusive lower bound ISO date/time string (e.g. "2024-01-01")
  date_from?: string | null;
  // Inclusive upper bound ISO date/time string
  date_to?: string | null;
}

// Base path for company-level customer endpoints
const BASE_COMPANY = "/customers/company";

// ---------- Overview ----------

export interface CustomerOverview {
  company_partner_id: number;
  company_name: string;
  salesperson_name: string | null;
  currency: string;
  total_gmv: number;
  order_count: number;
  average_order_value: number;
  first_order_date: string | null;
  last_order_date: string | null;
  last_invoice_date: string | null;
}

// ---------- Purchase trend ----------

export type PurchaseTrendPeriod = "month" | "day";

export interface PurchaseTrendPoint {
  // ISO date (start of month/day)
  period_start: string;
  total_amount: number;
  order_count: number;
}

export interface PurchaseTrendResponse {
  points: PurchaseTrendPoint[];
}

// ---------- Payment & aging ----------

export interface AgingBucket {
  label: string;
  days_from: number;
  days_to: number | null; // null = open ended
  outstanding_amount: number;
}

export interface PaymentSummaryResponse {
  customer_id: number;
  company_id: number;
  currency: string;
  total_billed: number;
  total_paid: number;
  total_outstanding: number;
  // Optional breakdown of outstanding amounts by aging buckets
  aging_buckets: AgingBucket[];
}

// ---------- UTM summary ----------

export interface UtmSourceSummary {
  source_id: number | null;
  source_name: string | null;
  gmv: number;
  order_count: number;
}

export interface UtmMediumSummary {
  medium_id: number | null;
  medium_name: string | null;
  gmv: number;
  order_count: number;
}

export interface UtmCampaignSummary {
  campaign_id: number | null;
  campaign_name: string | null;
  gmv: number;
  order_count: number;
}

export interface UtmSummaryResponse {
  customer_id: number;
  company_id: number;
  company_name: string;
  sources: UtmSourceSummary[];
  media: UtmMediumSummary[];
  campaigns: UtmCampaignSummary[];
}

// ---------- Contacts under company ----------

export interface CustomerContactItem {
  customer_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  country: string | null;
  order_count: number;
  gmv: number;
}

export interface CustomerContactsResponse {
  company_partner_id: number;
  company_name: string;
  contacts: CustomerContactItem[];
}

// ---------- API functions for Overview tab ----------

/**
 * Overview KPI for a company-level customer (company tab).
 */
export async function fetchCustomerOverview(
  companyPartnerId: number,
  params?: DateRangeQuery
): Promise<CustomerOverview> {
  const res = await http.get<CustomerOverview>(
    `${BASE_COMPANY}/${companyPartnerId}/overview`,
    { params }
  );
  return res.data;
}

/**
 * Purchase trend for a company-level customer.
 */
export async function fetchPurchaseTrend(
  companyPartnerId: number,
  period: PurchaseTrendPeriod,
  params?: DateRangeQuery
): Promise<PurchaseTrendResponse> {
  const res = await http.get<PurchaseTrendResponse>(
    `${BASE_COMPANY}/${companyPartnerId}/purchase-trend`,
    {
      params: {
        period,
        ...params,
      },
    }
  );
  return res.data;
}

/**
 * Payment summary + aging for a company-level customer.
 */
export async function fetchPaymentSummary(
  companyPartnerId: number,
  params?: DateRangeQuery
): Promise<PaymentSummaryResponse> {
  const res = await http.get<PaymentSummaryResponse>(
    `${BASE_COMPANY}/${companyPartnerId}/payment-summary`,
    { params }
  );
  return res.data;
}

/**
 * UTM (source / medium / campaign) summary.
 */
export async function fetchUtmSummary(
  companyPartnerId: number,
  params?: DateRangeQuery
): Promise<UtmSummaryResponse> {
  const res = await http.get<UtmSummaryResponse>(
    `${BASE_COMPANY}/${companyPartnerId}/utm-summary`,
    { params }
  );
  return res.data;
}

/**
 * Contacts under a company with basic stats (orders + GMV).
 */
export async function fetchCustomerContacts(
  companyPartnerId: number,
  params?: DateRangeQuery
): Promise<CustomerContactsResponse> {
  const res = await http.get<CustomerContactsResponse>(
    `${BASE_COMPANY}/${companyPartnerId}/contacts`,
    { params }
  );
  return res.data;
}
